import { ApiRequestError } from "@nexxt/api-client";
import { type CommonErrorCode, versionConflictDetails } from "@nexxt/contracts";
import { formatDate, formatDateTime, formatTime, toast } from "@nexxt/ui";
import { m } from "./paraglide/messages.js";
import { isUndoing } from "./undo/undo";

// The words of an error (ADR-046): the codes every gateway can answer with are here; an app adds the codes of the
// domains it reaches, in its own words (Atelier explains what Backstage names), with `registerErrorTexts` at start.

/** A text for every code: an app's table typed on its codes, so a code without one does not compile. */
export type ErrorTexts<Code extends string> = Record<Exclude<Code, CommonErrorCode>, () => string>;

const commonTexts: Record<CommonErrorCode, () => string> = {
  validation_error: m.error_validation_error,
  not_found: m.error_not_found,
  forbidden: m.error_forbidden,
  requester_cannot_approve: m.error_requester_cannot_approve,
  field_read_only: m.error_field_read_only,
  conflict: m.error_conflict,
  invalid_transition: m.error_invalid_transition,
  unique_violation: m.error_unique_violation,
  foreign_key_violation: m.error_foreign_key_violation,
  check_violation: m.error_check_violation,
  not_null_violation: m.error_not_null_violation,
  invalid_input: m.error_invalid_input,
  query_canceled: m.error_query_canceled,
  http_error: m.error_unexpected,
  internal_error: m.error_unexpected,
};

let codeTexts: Readonly<Record<string, () => string>> = commonTexts;

/** The app's texts of its domains' codes, once, before the first render (main.tsx). */
export function registerErrorTexts<Code extends string>(texts: ErrorTexts<Code>): void {
  codeTexts = { ...commonTexts, ...texts };
}

/** What went wrong, in the app's words, with the request id to quote to support (design-system.md §4.12). */
export function errorText(error: unknown): string {
  if (!(error instanceof ApiRequestError)) return m.error_unexpected();
  const message = errorCodeText(error.code) ?? error.message;
  return error.requestId ? m.error_with_request_id({ message, requestId: error.requestId }) : message;
}

/** The words of a code an outcome carries without a request of its own (a bulk write); null for an unknown code. */
export function errorCodeText(code: string): string | null {
  return Object.hasOwn(codeTexts, code) ? (codeTexts[code]?.() ?? null) : null;
}

/**
 * Tells the user a write failed, unless the screen does: what a form or a dialog shows next to its fields stays
 * there (every 422, and the codes in `shownByScreen`). A 409 conflict means someone changed what the user was looking
 * at: it is reloaded, `changed` says so in the words of the screen, and the line under it who did and when. With the
 * variables of an undo (`vars`, ADR-282) every refusal is said here, as "Not undone": the screen shows the refusals
 * of the save it asked, not of the undo a toast ran.
 */
export function reportError(
  error: unknown,
  changed: () => string,
  shownByScreen: readonly string[] = [],
  vars?: unknown,
) {
  if (!(error instanceof ApiRequestError)) return void toast.error(errorText(error));
  if (isUndoing(vars)) {
    const conflict = error.code === "conflict";
    const why = conflict ? lastChangeText(error) : errorText(error);
    return void toast.warning((conflict ? m.undo_refused : m.undo_failed)(), why ? { description: why } : {});
  }
  if (error.status === 422 || shownByScreen.includes(error.code)) return;
  if (error.code !== "conflict") return void toast.error(errorText(error));
  const who = lastChangeText(error);
  toast.warning(changed(), who ? { description: who } : {});
}

/**
 * Who wrote the row of an optimistic lock last, and when, as the domain answered the 409 (ADR-282): the name only of
 * a person the user may see. Null when the 409 does not say.
 */
export function lastChangeText(error: ApiRequestError, today = new Date()): string | null {
  const details = versionConflictDetails.safeParse(error.details);
  if (!details.success) return null;
  const { changedAt, changedBy } = details.data;
  const sameDay = formatDate(changedAt) === formatDate(today.toISOString());
  const time = sameDay ? formatTime(changedAt) : formatDateTime(changedAt);
  return changedBy ? m.conflict_changed_by({ name: changedBy, time }) : m.conflict_changed_at({ time });
}
